import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { ArrowLeft, Sparkles, ShieldCheck, AlertTriangle, Phone, Mail, MapPin } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { Button } from "../components/ui/button";
import { toast, Toaster } from "sonner";

const TIER_LABEL = {
  merchant_lite: "Merchant Lite",
  social_vendor_pro: "Social Vendor Pro",
  enterprise_sme: "Enterprise SME",
};

export default function MerchantDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  function load() {
    api.get(`/admin/merchants/${id}`)
       .then((r) => setData(r.data))
       .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }
  useEffect(load, [id]);

  async function resumeLoyalty() {
    try {
      await api.patch(`/admin/loyalty/${id}/resume`);
      toast.success("Loyalty resumed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  if (err) {
    return (
      <div className="p-8" data-testid="merchant-detail-error">
        <div className="text-sm text-destructive bg-red-50 border border-red-200 rounded-sm p-3">{err}</div>
      </div>
    );
  }
  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const m = data.merchant;
  const snaps = (data.risk_snapshots || []).map((s) => ({
    week: new Date(s.captured_at).toLocaleDateString("en", { month: "short", day: "2-digit" }),
    score: s.cyber_score,
  }));
  const loyalty = data.loyalty || {};

  return (
    <div className="p-8 space-y-6" data-testid="merchant-detail-page">
      <Toaster position="top-right" />
      <button onClick={() => nav("/merchants")} className="text-sm text-stone-600 hover:text-primary inline-flex items-center gap-1.5" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> All merchants
      </button>

      {/* Header card */}
      <header className="bg-white border border-border rounded-md p-6 flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
            {m.business_type}
          </div>
          <h1 className="font-heading text-3xl font-bold mt-1">{m.business_name}</h1>
          <div className="text-sm text-stone-700 mt-1">{m.owner_name}</div>
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-stone-700">
            <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4 text-stone-400" /> <span className="font-mono">{m.phone}</span></span>
            <span className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4 text-stone-400" /> {m.email}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-stone-400" /> {m.country}</span>
          </div>
          <div className="flex gap-2 mt-4">
            <span className="badge-status badge-active">{m.status}</span>
            <span className="badge-status badge-success">KYC: {m.kyc_status}</span>
            <span className="badge-status badge-success">KYB: {m.kyb_status}</span>
            {m.loyalty_paused && (
              <span className="badge-status badge-suspended" data-testid="loyalty-paused-badge">Loyalty paused</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">CyberScore</div>
          <div className="font-mono text-5xl font-bold text-primary leading-none mt-1">{m.risk_score}</div>
          <div className="text-xs text-muted-foreground mt-1">Risk index 0-100</div>
        </div>
      </header>

      {/* Streak chart + Loyalty status */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-border rounded-md p-5 lg:col-span-2" data-testid="streak-chart">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading text-base font-semibold">12-week CyberScore</div>
            <div className="text-xs text-muted-foreground">Threshold {loyalty.threshold ?? 80}</div>
          </div>
          {snaps.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={snaps} margin={{ top: 10, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                <XAxis dataKey="week" stroke="#78716c" fontSize={11} />
                <YAxis domain={[0, 100]} stroke="#78716c" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #e7e5e4", borderRadius: 4, fontSize: 12 }}
                  cursor={{ stroke: "#14532d", strokeWidth: 1, strokeOpacity: 0.2 }}
                />
                <ReferenceLine y={80} stroke="#ca8a04" strokeDasharray="4 4" label={{ value: "Rebate threshold", position: "right", fill: "#ca8a04", fontSize: 10 }} />
                <Line type="monotone" dataKey="score" stroke="#14532d" strokeWidth={2.5} dot={{ r: 4, fill: "#14532d" }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="py-12 text-center text-muted-foreground text-sm">No snapshots yet</div>
          )}
        </div>

        <div className="bg-white border border-border rounded-md p-5" data-testid="loyalty-card">
          <div className="font-heading text-base font-semibold mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" /> Loyalty Rebate
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</div>
              <div className="mt-1">
                {loyalty.eligible ? (
                  <span className="badge-status badge-success inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Eligible</span>
                ) : loyalty.paused ? (
                  <span className="badge-status badge-suspended inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Paused</span>
                ) : (
                  <span className="badge-status badge-pending">Building streak</span>
                )}
              </div>
              {loyalty.pause_reason && (
                <div className="text-xs text-muted-foreground mt-1">{loyalty.pause_reason}</div>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Last 4 scores</div>
              <div className="flex gap-1.5 mt-1.5">
                {(loyalty.last_scores || []).map((s, i) => (
                  <span key={i} className={`font-mono text-xs px-2 py-0.5 rounded-sm ring-1 ${s >= 80 ? "bg-emerald-50 text-emerald-800 ring-emerald-200" : "bg-amber-50 text-amber-800 ring-amber-200"}`}>{s}</span>
                ))}
                {(loyalty.last_scores || []).length === 0 && <span className="text-xs text-muted-foreground">none</span>}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Rebates received</div>
              <div className="mt-1 font-mono">{loyalty.rebates?.length || 0}</div>
            </div>
            {loyalty.paused && (
              <Button
                onClick={resumeLoyalty} size="sm"
                className="w-full mt-3 bg-primary hover:bg-primary/90"
                data-testid="resume-loyalty-btn"
              >
                Resume loyalty
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Policies */}
      <section className="bg-white border border-border rounded-md" data-testid="policies-section">
        <div className="px-5 py-4 border-b border-border font-heading text-base font-semibold">Policies</div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-stone-500">
            <tr>
              <th className="text-left font-medium py-2 px-5">Policy #</th>
              <th className="text-left font-medium py-2 px-5">Tier</th>
              <th className="text-left font-medium py-2 px-5">Cover</th>
              <th className="text-left font-medium py-2 px-5">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.policies.map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="py-2 px-5 font-mono text-xs">{p.policy_number}</td>
                <td className="py-2 px-5">{TIER_LABEL[p.tier] || p.tier}</td>
                <td className="py-2 px-5 font-mono">KES {p.cover_limit.toLocaleString()}</td>
                <td className="py-2 px-5"><span className="badge-status badge-active">{p.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Recent rebates */}
      {(loyalty.rebates || []).length > 0 && (
        <section className="bg-white border border-border rounded-md" data-testid="rebate-history-section">
          <div className="px-5 py-4 border-b border-border font-heading text-base font-semibold">Rebate history</div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-stone-500">
              <tr>
                <th className="text-left font-medium py-2 px-5">Applied</th>
                <th className="text-left font-medium py-2 px-5">Original</th>
                <th className="text-left font-medium py-2 px-5">Discount</th>
                <th className="text-left font-medium py-2 px-5">Final</th>
              </tr>
            </thead>
            <tbody>
              {loyalty.rebates.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="py-2 px-5 text-xs text-muted-foreground font-mono">
                    {new Date(r.applied_at).toLocaleString()}
                  </td>
                  <td className="py-2 px-5 font-mono">KES {r.original_amount}</td>
                  <td className="py-2 px-5 font-mono text-emerald-700">−KES {r.rebate_amount}</td>
                  <td className="py-2 px-5 font-mono font-semibold">KES {r.final_amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
